import { FileInput, FilePlus, FileSpreadsheet, Plus, Upload } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { CLEAR_QUOTE_WIDGET_PAYLOAD } from '../../../states/slices/quoteWidgetSlice';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import useUserAccount from '../../../hooks/userAccountHook';
import useRoute from '../../../hooks/routesHook';
import { useNavigate } from 'react-router-dom';
import QuoteWidgetCalculator from '../../../views/admin/quotes/calculators/widgetChecker/components/Calculator';
import useModal from '../../../hooks/modalHook';
import useLayoutHook from '../../../hooks/layoutHook';
import { BROKER_MAPPING_TOOL_UPLOAD } from '../../../resources/links';

interface AddPolicyButtonProps {
  isSidebarExpanded: boolean;
}

export default function AddPolicyButton({ isSidebarExpanded }: AddPolicyButtonProps) {
  const { user } = useUserAccount();
  const dispatch = useDispatch();
  const { getDashboardBase } = useRoute();
  const navigate = useNavigate();
  const { screenWidth } = useLayoutHook();
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const [showPolicyOptions, setShowPolicyOptions] = useState<boolean>(false);
  const [openActionsModal, setOpenActionsModal] = useState<boolean>(false)

  if (!user.isBroker()) return null;

  const handleNonMotorPolicyClick = () => {
    setShowPolicyOptions(false);
    navigate(`${getDashboardBase()}/policies/dynamic-create-policy?type=non-motor`);
  };
  const getActions = () => {
    let actions = [
      {
        label: 'Create a new policy',
        icon: <FilePlus className="h-4 w-4" />,
        callback: () => {
          updateModalStates({
            show: true,
            size: '4xl',
            children: <QuoteWidgetCalculator onclose={closeModal}/>,
            static: true,
            title: screenWidth <= 550 ? 'Motor Insurance Calculator' : 'Motor Insurance Premium Calculator',
          });
        },
      },
      {
        label: 'Add an existing policy',
        icon: <FileInput className="h-4 w-4" />,
        callback: () => navigate(`${getDashboardBase()}/policies/auto-policy/add-issued-motor-policy`)
      },
      // "Import broker legacy data" entry — temporarily disabled.
      // Uncomment to expose the legacy bulk-import flow under Add Policy → Motor Policy.
      // {
      //   label: 'Import existing policies in bulk',
      //   icon: <Upload className="h-4 w-4" />,
      //   callback: () => navigate(`${getDashboardBase()}/policies/import-policies`)
      // },
      {
        label: 'Upload bulk data',
        icon: <FileSpreadsheet className="h-4 w-4" />,
        callback: () => navigate(BROKER_MAPPING_TOOL_UPLOAD)
      },
    ];

    return actions;
  };

  return (
    <div className="px-5 py-2">
      <button
        onClick={() => {
          dispatch(CLEAR_QUOTE_WIDGET_PAYLOAD());
          setShowPolicyOptions((prev: boolean) => !prev);
        }}
        className="flex w-full items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg bg-primary-main hover:bg-opacity-90 transition-all duration-200 shadow-sm hover:shadow-md"
      >
        <Plus className="h-4 w-4 mr-2" />
        {isSidebarExpanded && 'Add Policy'}
      </button>

      <AnimatePresence>
        {showPolicyOptions && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="mt-2 rounded-lg border border-gray-200 dark:border-darkBg-border bg-white dark:bg-darkBg shadow-sm"
          >
            <p className="text-xs font-medium text-gray-600 dark:text-darkBg-subText px-3 pt-3 pb-2">
              Select policy type
            </p>
            <div className="px-2 ">
              <div
                onMouseEnter={()=> setOpenActionsModal(true)}
                onMouseLeave={()=>setOpenActionsModal(false)}
              >
                {/* BUTTON */}
                <button className={`block w-full rounded-md px-3 py-2 text-left text-sm font-medium text-gray-700 dark:text-darkBg-mainText ${openActionsModal && 'bg-primary-surface dark:bg-darkBg-border'} hover:bg-primary-surface dark:hover:bg-darkBg-border transition-colors duration-150`}>
                  Motor Policy
                </button>
                {/* ACTIONS MODAL */}
                {openActionsModal && 
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }} 
                    className="relative top-0"
                  >
                    <ul className="py-2 absolute top-0 left-36 w-[280px] z-50  bg-white shadow-md rounded-md border border-gray-200 ">
                      {getActions().map((action, index) => (
                        <li
                          key={index}
                          onClick={()=>{
                            action.callback();
                            setOpenActionsModal(false)
                          }}
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-primary-surface hover:text-black cursor-pointer transition-all duration-150"
                        >
                          <span className="text-lg">{action.icon}</span>
                          <span className="font-medium">{action.label}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                }
              </div>
           
        
              <button
                className="block w-full rounded-md px-3 py-2  mb-2 text-left text-sm font-medium text-gray-700 dark:text-darkBg-mainText hover:bg-primary-surface dark:hover:bg-darkBg-border transition-colors duration-150"
                onClick={handleNonMotorPolicyClick}
              >
                Non-Motor Policy
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <ModalComponent/>
    </div>
  );
}
